import {
  Box,
  Divider,
  useColorModeValue,
  Text,
  VStack,
  Stack,
  Button,
  Skeleton,
  Image,
  useBreakpointValue,
  Center,
} from "@chakra-ui/react"; // Add Spinner
import React, { useEffect, useCallback, useState } from "react";
import { DealTile } from "screens/dealfinder/components/DealTile";
import { getDealFinderUserActions } from "api/tickers";
import type { DealFinderTicker } from "api/tickers/models/DealFinderTicker";
import { useButtonProps, useSkeletonProps, useTileProps, useUserProfile } from "hooks";
import { useIsWebsocketConnected } from "hooks/useWebsocket";
import { getWebsocketClient } from "api/websocket/client";
import type { TopicSubscription } from "api/websocket/TopicSubscription";
import dealsIcon from "screens/common/static/misc/deals_icon.svg";
import { useNavigate } from "react-router-dom";

export const DealWatchListGrid = () => {
  const cardBgColor = useColorModeValue("white", "gray.900");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("primary.darkGray", "gray.400");
  const primaryButtonProps = useButtonProps("sm", "primary");
  const [deals, setDeals] = useState<DealFinderTicker[]>([]);
  const { id } = useUserProfile();
  const [isLoading, setIsLoading] = useState(true); // Add loading state, default true
  const skeletonStyle = useSkeletonProps();
  const isWebsocketConnected = useIsWebsocketConnected();
  const navigate = useNavigate();
  const isMobile = useBreakpointValue({ base: true, md: false }, { fallback: "md", ssr: false });
  const commonTileProps = useTileProps();

  // Callback function to fetch deals
  const fetchWatchListDeals = useCallback(
    async (isRefresh = false) => {
      if (!id) return; // Ensure id is available before fetching

      // Set loading state ONLY if it's not a refresh AND there are no deals currently displayed.
      // This prevents the skeleton from showing if data already exists, even if a remount occurs.
      if (!isRefresh && deals.length === 0) {
        setIsLoading(true); // Set loading true before fetch
      }

      try {
        // Use the function with userId and actionType
        const fetchedDeals = await getDealFinderUserActions({
          userId: id,
          actionType: "watch",
        });
        setDeals(fetchedDeals);
      } catch (error) {
        setDeals([]); // Clear deals on error
      } finally {
        // Ensure loading is set to false even if it wasn't set to true initially
        setIsLoading(false); // Set loading false after fetch completes or fails
      }
    },
    [id, deals.length] // Dependency: id and deals.length (for the conditional loading)
  );

  // Initial fetch on component mount
  useEffect(() => {
    fetchWatchListDeals();
  }, [fetchWatchListDeals]); // Dependency: fetchWatchListDeals callback

  // Subscribe to WebSocket event for deals refresh
  useEffect(() => {
    let sub: TopicSubscription | undefined;

    // Only subscribe if the websocket is connected and fetch function is available
    if (isWebsocketConnected && fetchWatchListDeals) {
      try {
        const wsClient = getWebsocketClient();
        // Define the type for the payload if known, otherwise use any or a generic type
        sub = wsClient.subscribeToEvent<{ dealId: string }>("deals/refresh", (payload) => {
          fetchWatchListDeals(true); // Pass true to indicate this is a refresh
        });
      } catch (error) {
        // Optionally, implement retry logic here
      }
    } else {
      console.log("Websocket not connected or fetch function not ready, skipping subscription.");
    }

    // Cleanup function to unsubscribe on component unmount or when dependencies change
    return () => {
      if (sub) {
        try {
          sub.unsubscribe();
        } catch (error) {
          console.warn("Error unsubscribing from deals/refresh:", error);
        }
      }
    };
  }, [fetchWatchListDeals, isWebsocketConnected]);

  return (
    <Box className="ch-deal-watch-list-grid" {...commonTileProps} p="0" bgColor={cardBgColor} overflow="hidden" width="100%" height="100%">
      <Text fontWeight="600" fontSize="md" lineHeight="1.4" color={textColor} px="1rem" pt="1rem">
        Watch List
      </Text>
      <VStack spacing={0} divider={<Divider borderColor={borderColor} m="0!important" />} align="stretch" minH="100px">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={`skeleton-${index}`} {...skeletonStyle} px="2px" my="2px" borderRadius={"none"} height="66.4px" />
          ))
        ) : deals && deals.length > 0 ? (
          deals.map((deal) => (
            <Box key={`deal-${deal.id}`}>
              <DealTile deal={deal} displayOption="list" />
            </Box>
          ))
        ) : (
          <Center py="3rem" textAlign="center">
            <Button
              {...primaryButtonProps}
              leftIcon={<Image src={dealsIcon} boxSize="1.5rem" pb="1px" />}
              onClick={() => navigate("/deal-finder")}>
              Deal Finder
            </Button>
          </Center>
        )}
      </VStack>

      <Stack
        onClick={() => navigate("/deal-finder")}
        direction="row"
        spacing={isMobile ? ".5rem" : "1rem"}
        width="100%"
        height="2rem"
        px={"1rem"}
        py={".5rem"}
        borderTop="1px solid"
        borderColor={borderColor}
        align="center">
        <Button
          {...primaryButtonProps}
          pt="1px"
          border="none"
          paddingInline={"0"}
          _hover={{}}
          _active={{}}
          height="unset"
          leftIcon={<Image src={dealsIcon} boxSize="1rem" pb="1px" />}
          onClick={() => navigate("/deal-finder")}>
          Deal Finder
        </Button>
      </Stack>
    </Box>
  );
};
